import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, CircularProgress, Drawer } from '@mui/material';
import { Plus, UserPlus } from 'lucide-react';
import {
  deleteOrganisation,
  fetchOrganisations,
  setSelectedOrganisation,
} from '../../store/slices/organizationSlice';
import { Organisation } from '../../types/organization';
import { ROUTES } from '../../config/constants/routes';
import OrganizationList from './components/OrganizationList';
import OrganizationAddView from '../Organizations/OrganizationAddView';
import SuperAdminLayout from '../../components/layout/SuperAdmin/SuperAdminLayout';
import ConfirmationDialog from '../../components/common/ConfirmationDialog';
import Notifications from '../../components/common/Notifications';
import { organizationEndpoints } from '../../services/api/endpoints/organisation.endpoint';
import { dealerEndpoints, CreateDealerDTO } from '../../services/api/endpoints/dealer.endpoint';
import { AppDispatch } from '../../store';
import { NotificationT, SEVERITY } from '../../types/notification';
import { setSelectedOrganizationId } from '../../store/slices/organizationContextSlice';
import DealerForm from './components/DealerForm';

const SuperAdminView = () => {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();

  // Drawer state
  const [isAddDrawerOpen, setIsAddDrawerOpen] = useState(false);
  const [isDealerDrawerOpen, setIsDealerDrawerOpen] = useState(false);
  const [isDealerSubmitting, setIsDealerSubmitting] = useState(false);

  const [isDelete, setIsDelete] = useState(false);

  // Confirmation Dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    orgId: '',
  });

  // Notification state
  const [notification, setNotification] = useState<NotificationT>({
    open: false,
    message: '',
    severity: SEVERITY.SUCCESS,
  });

  const handleSelect = (org: Organisation) => {
    dispatch(setSelectedOrganisation(org));
    dispatch(setSelectedOrganizationId(org.id));
    navigate(ROUTES.DASHBOARD);
  };

  const handleDelete = (id: string) => {
    setConfirmDialog({
      open: true,
      orgId: id,
    });
  };

  const handleDeleteConfirm = async () => {
    setIsDelete(true);
    const deletedOrganisations = await organizationEndpoints.deleteOrganisation(
      confirmDialog.orgId
    );

    dispatch(deleteOrganisation(confirmDialog.orgId));
    setConfirmDialog({ open: false, orgId: '' });
    setIsDelete(false);
    setNotification({
      open: true,
      message: 'Organisation deleted successfully',
      severity: SEVERITY.SUCCESS,
    });

    return deletedOrganisations;
  };

  const handleAddSuccess = async () => {
    dispatch(fetchOrganisations());
    setNotification({
      open: true,
      message: 'Organisation created successfully',
      severity: SEVERITY.SUCCESS,
    });
  };

  const handleCreateDealer = async (data: CreateDealerDTO) => {
    setIsDealerSubmitting(true);
    try {
      await dealerEndpoints.createDealer(data);
      setNotification({
        open: true,
        message: 'Dealer created successfully. An email with temporary password has been sent.',
        severity: SEVERITY.SUCCESS,
      });
      setIsDealerDrawerOpen(false);
    } catch (error: any) {
      const message = error?.message || 'Failed to create dealer';
      setNotification({
        open: true,
        message,
        severity: SEVERITY.ERROR,
      });
    } finally {
      setIsDealerSubmitting(false);
    }
  };

  return (
    <SuperAdminLayout>
      <Box sx={{ p: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 4,
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 500 }}>
            Organizations
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<UserPlus size={20} />}
              onClick={() => setIsDealerDrawerOpen(true)}
            >
              Create Dealer
            </Button>
            <Button
              variant="contained"
              disableElevation
              startIcon={<Plus size={20} />}
              onClick={() => setIsAddDrawerOpen(true)}
            >
              Add Organisation
            </Button>
          </Box>
        </Box>
        {isDelete ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              width: '100%',
            }}
          >
            <CircularProgress size={50} thickness={4} className="text-purple-500 mb-4" />
          </Box>
        ) : (
          <>
            <OrganizationList onEdit={() => {}} onDelete={handleDelete} onSelect={handleSelect} />

            {/* Add Organisation Drawer */}
            <OrganizationAddView
              open={isAddDrawerOpen}
              onClose={() => setIsAddDrawerOpen(false)}
              onSuccess={handleAddSuccess}
            />

            {/* Create Dealer Drawer */}
            <Drawer
              anchor="right"
              open={isDealerDrawerOpen}
              onClose={() => !isDealerSubmitting && setIsDealerDrawerOpen(false)}
            >
              <DealerForm
                onSubmit={handleCreateDealer}
                onClose={() => setIsDealerDrawerOpen(false)}
                isLoading={isDealerSubmitting}
              />
            </Drawer>

            {/* Confirmation Dialog */}
            <ConfirmationDialog
              open={confirmDialog.open}
              title="Delete Organisation"
              message="Are you sure you want to delete this organisation? This action cannot be undone."
              onConfirm={handleDeleteConfirm}
              onCancel={() => setConfirmDialog({ open: false, orgId: '' })}
            />
          </>
        )}

        {/* Notifications */}
        <Notifications
          open={notification.open}
          message={notification.message}
          severity={notification.severity}
          onClose={() => setNotification(prev => ({ ...prev, open: false }))}
        />
      </Box>
    </SuperAdminLayout>
  );
};

export default SuperAdminView;
